/**
 * A tela de um colaborador do banco de horas.
 *
 * Papel: reproduz `bhRenderEmployee` da origem — o "← Voltar ao resumo", o
 * cabeçalho com o nome, a linha de função, departamento e admissão, o saldo
 * atual e os botões do colaborador, e a moldura com a contagem de meses, o
 * "+ Adicionar mês" e a tabela do mês mais antigo ao mais novo, com o saldo do
 * mês e o acumulado.
 *
 * Como usar: cada botão só aparece com a ação dele ligada; quem não pode gravar
 * recebe só `onVoltar` e `onImprimir`. A coluna de ações da tabela existe
 * sempre, vazia sem `onEditarMes` e `onRemoverMes`, como a coluna `no-print`
 * da origem.
 *
 * A faixa com "N MESES REGISTRADOS" fica dentro da mesma borda da tabela, e por
 * isso a borda própria do `Table` é desligada ali dentro — sem isso, a moldura
 * sairia dupla.
 *
 * Crédito em verde, débito em vermelho, ajuste em latão; saldo do mês, saldo
 * acumulado e saldo atual em verde a partir de zero e em vermelho abaixo, por
 * `corDoSaldo`. O saldo atual pesa 600 (`font-semibold`): a origem pede 700, mas
 * só carrega o Inter até 600, e o navegador o desenha com a face 600 — com o 700
 * de verdade, o número sairia mais grosso e mais largo.
 *
 * Todo botão leva a entrelinha `normal` (`text-sm/[normal]`, `text-xs/[normal]`),
 * a do `.btn` da origem, que não a fixa: com a do `Button`, cada um sairia 3px
 * mais alto, e o "🖨 Imprimir / PDF", que cresce com o emoji, deixaria de puxar
 * os vizinhos para a altura dele. Fora os da linha do mês, que a célula da
 * origem não deixa quebrar, eles também encolhem e quebram o texto em tela
 * estreita (`shrink`, `whitespace-normal`), como o `.btn`; com o `Button` como
 * vem, o "remover" do cabeçalho sairia da tela a 375px.
 */
import {
  type ColaboradorBancoHoras,
  type LancamentoMensal,
  saldoEmMinutos,
} from '@concierge/shared';

import { formatarCompetencia } from '@/shared/lib/formato';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button';
import { CabecalhoDeSecao } from '@/shared/ui/cabecalho-de-secao';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import {
  comAcumulado,
  corDoSaldo,
  formatarAjuste,
  formatarDias,
  formatarHoras,
  linhaDoColaborador,
} from '../banco-horas';

interface DetalheBancoHorasProps {
  colaborador: ColaboradorBancoHoras;
  /** O "← Voltar ao resumo". */
  onVoltar: () => void;
  /** Imprime o detalhamento; ausente esconde o botão. */
  onImprimir?: () => void;
  /** Abre a caixa do colaborador; ausente esconde o botão. */
  onEditarColaborador?: () => void;
  /** Pede a remoção do colaborador; ausente esconde o botão. */
  onRemoverColaborador?: () => void;
  /** Abre a caixa de mês novo; ausente esconde o botão. */
  onAdicionarMes?: () => void;
  /** Abre a caixa do mês da linha; ausente esconde o botão. */
  onEditarMes?: (mes: LancamentoMensal) => void;
  /** Pede a remoção do mês da linha; ausente esconde o botão. */
  onRemoverMes?: (mes: LancamentoMensal) => void;
}

/**
 * A tela do colaborador.
 *
 * @param colaborador - O colaborador, com os meses.
 * @param onVoltar - Volta ao resumo.
 * @param onImprimir - O "🖨 Imprimir / PDF".
 * @param onEditarColaborador - O "editar colaborador".
 * @param onRemoverColaborador - O "remover" do cabeçalho.
 * @param onAdicionarMes - O "+ Adicionar mês".
 * @param onEditarMes - O "editar" da linha, com o mês dela.
 * @param onRemoverMes - O "remover" da linha, com o mês dela.
 * @returns O voltar e o bloco do colaborador.
 */
export function DetalheBancoHoras({
  colaborador,
  onVoltar,
  onImprimir,
  onEditarColaborador,
  onRemoverColaborador,
  onAdicionarMes,
  onEditarMes,
  onRemoverMes,
}: DetalheBancoHorasProps) {
  const linhas = comAcumulado(colaborador.meses);
  const saldo = saldoEmMinutos(colaborador.meses);

  return (
    <>
      <Button
        variant="outline"
        className="shrink text-center text-sm/[normal] whitespace-normal"
        onClick={onVoltar}
      >
        ← Voltar ao resumo
      </Button>

      <section className="mt-[18px] mb-9">
        <CabecalhoDeSecao
          titulo={colaborador.nomeCompleto}
          subtitulo={linhaDoColaborador(colaborador).toUpperCase()}
          acoes={
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-[10px] tracking-[0.05em] text-texto-fraco">
                  SALDO ATUAL
                </div>
                <div className={cn('text-[18px] font-semibold', corDoSaldo(saldo))}>
                  {formatarHoras(saldo)}
                </div>
              </div>
              <div className="flex gap-2.5">
                {onImprimir !== undefined && (
                  <Button
                    variant="outline"
                    className="shrink text-center text-sm/[normal] whitespace-normal"
                    onClick={onImprimir}
                  >
                    🖨 Imprimir / PDF
                  </Button>
                )}
                {onEditarColaborador !== undefined && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink text-center text-xs/[normal] whitespace-normal"
                    onClick={onEditarColaborador}
                  >
                    editar colaborador
                  </Button>
                )}
                {onRemoverColaborador !== undefined && (
                  <Button
                    variant="destructive"
                    className="shrink text-center text-xs/[normal] whitespace-normal"
                    onClick={onRemoverColaborador}
                  >
                    remover
                  </Button>
                )}
              </div>
            </div>
          }
        />

        <div className="border border-linha-forte [&_[data-slot=table-container]]:border-0">
          <div className="flex items-center justify-between border-b border-linha bg-superficie px-4 py-3">
            <span className="font-mono text-[11px] tracking-[0.04em] text-texto-fraco">
              {linhas.length} MESES REGISTRADOS
            </span>
            {onAdicionarMes !== undefined && (
              <Button
                className="shrink text-center text-sm/[normal] whitespace-normal"
                onClick={onAdicionarMes}
              >
                + Adicionar mês
              </Button>
            )}
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Período</TableHead>
                <TableHead>Crédito</TableHead>
                <TableHead>Débito</TableHead>
                <TableHead>Ajuste</TableHead>
                <TableHead>Saldo mês</TableHead>
                <TableHead>Saldo acumulado</TableHead>
                <TableHead>
                  <span className="sr-only">Ações</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {linhas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="p-6 text-center text-texto-fraco">
                    Nenhum registro ainda.
                  </TableCell>
                </TableRow>
              ) : (
                linhas.map(({ mes, saldoDoMes, acumulado }) => (
                  <TableRow key={mes.id}>
                    <TableCell className="font-semibold">
                      {formatarCompetencia(mes.competencia)}
                    </TableCell>
                    <TableCell className="font-semibold text-sucesso">
                      {formatarHoras(mes.creditoMin)}
                    </TableCell>
                    <TableCell className="font-semibold text-perigo">
                      {formatarHoras(mes.debitoMin)}
                    </TableCell>
                    <TableCell>
                      <span
                        className={
                          mes.ajusteMin === 0
                            ? 'text-texto-fraco'
                            : 'font-semibold text-brass-light'
                        }
                      >
                        {formatarAjuste(mes.ajusteMin)}
                      </span>
                    </TableCell>
                    <TableCell className={cn('font-semibold', corDoSaldo(saldoDoMes))}>
                      {formatarHoras(saldoDoMes)}
                    </TableCell>
                    <TableCell>
                      <div className={cn('font-semibold', corDoSaldo(acumulado))}>
                        {formatarHoras(acumulado)}
                      </div>
                      <div className="text-[11px] text-texto-fraco">
                        {formatarDias(acumulado)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-baseline justify-end gap-1">
                        {onEditarMes !== undefined && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="px-[11px] py-[5px] text-xs/[normal]"
                            onClick={() => onEditarMes(mes)}
                          >
                            editar
                          </Button>
                        )}
                        {onRemoverMes !== undefined && (
                          <Button
                            variant="destructive"
                            className="text-xs/[normal]"
                            onClick={() => onRemoverMes(mes)}
                          >
                            remover
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </section>
    </>
  );
}
